import { BodyText, Button, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ErrorText,
  ErrorTextContainer,
  StyledCaption,
  StyledInput,
  StyledTooltip
} from 'app/components/common/common-styles';
import styled from 'styled-components';
import { createRewardsType, updateRewardsType } from 'app/console-api/apps/rewards';
import { StyledActionIcon } from 'app/components/common/table/styled-elements';
import { AppEnvs } from 'config/env';
import { RewardsType, RewardsTypeCreateRequest } from 'app/console-api/apps/types';
import InfoIcon from 'assets/icons/ic-info.svg';

// for the key-only `type`
const KEY_REGEX = /^[A-Za-z_1-9]+$/;

interface RewardsTypesControlProps {
  appID: string;
  /**
   * Optional: provide initial rewards types from parent to avoid fetching.
   * If omitted, the component will load them via API on mount.
   */
  initialRewards?: RewardsType[];
}

interface RowState {
  id?: number;
  type: string;
  name: string;
  isNew: boolean;
  isEdited: boolean;
  draftType: string;
  draftName: string;
}

const StyledAddElement = styled(BodyText)<{ isPointer?: boolean }>(({ theme, isPointer }) =>
  theme.withMedia({
    cursor: isPointer ? 'pointer' : 'default',
    fontWeight: '400',
    lineHeight: '24px',
    marginBottom: '12px',
    '&:hover': {
      color: isPointer
        ? theme.styleguideColors.fillPrimaryBlueClick
        : theme.styleguideColors.contentTertiary
    }
  })
);

export const RewardsTypesControl: React.FC<RewardsTypesControlProps> = ({
  appID,
  initialRewards
}) => {
  const [rows, setRows] = useState<RowState[]>([]);
  const [errors, setErrors] = useState(new Map<number, string>());

  const limit = AppEnvs.rewardsTypeLimit;

  useEffect(() => {
    if (initialRewards && initialRewards.length >= 0) {
      const prepared = initialRewards.map((rt: any) => ({
        id: rt.id,
        type: rt.type ?? rt.name, // fallback if older shape
        name: rt.name,
        isNew: false,
        isEdited: false,
        draftType: rt.type ?? rt.name,
        draftName: rt.name
      }));
      setRows(prepared);
      return;
    }
  }, [appID, initialRewards]);

  const validateOne = (idx: number, draftType: string, draftName: string): boolean => {
    const updatedErrors = new Map(errors);
    const t = (draftType ?? '').trim();
    const n = (draftName ?? '').trim();

    if (!t) {
      updatedErrors.set(idx, 'Type is required');
      setErrors(updatedErrors);
      return false;
    }
    if (!n) {
      updatedErrors.set(idx, 'Name is required');
      setErrors(updatedErrors);
      return false;
    }
    if (t.length < 1 || t.length > 36) {
      updatedErrors.set(idx, 'Type must be 1–36 characters');
      setErrors(updatedErrors);
      return false;
    }
    if (n.length < 1 || n.length > 64) {
      updatedErrors.set(idx, 'Name must be 1–64 characters');
      setErrors(updatedErrors);
      return false;
    }
    if (!KEY_REGEX.test(t)) {
      updatedErrors.set(idx, 'Type may only use letters, numbers, and underscore (_)'); // key-safe
      setErrors(updatedErrors);
      return false;
    }
    // Uniqueness on `type`
    if (rows.filter((r) => r.draftType.trim() === t).length > 1) {
      updatedErrors.set(idx, 'Type must be unique');
      setErrors(updatedErrors);
      return false;
    }

    updatedErrors.delete(idx);
    setErrors(updatedErrors);
    return true;
  };

  const onEdit = (index: number) => {
    const updated = [...rows];
    updated[index].isEdited = true;
    setRows(updated);
  };

  const onCancelEdit = (index: number) => {
    const updated = [...rows];
    const row = updated[index];
    row.isEdited = false;
    row.draftType = row.type;
    row.draftName = row.name;
    validateOne(index, row.draftType, row.draftName);
    setRows(updated);
  };

  const onSaveEdit = (index: number) => {
    const updated = [...rows];
    const row = updated[index];
    if (!validateOne(index, row.draftType, row.draftName)) return;

    const payload: RewardsTypeCreateRequest = {
      type: row.draftType.trim(),
      name: row.draftName.trim()
    } as RewardsTypeCreateRequest;

    updateRewardsType(appID, row.id || 0, payload)
      .then(() => {
        row.type = payload.type;
        row.name = payload.name;
        row.isEdited = false;
        updated[index] = { ...row };
        setRows(updated);
      })
      .catch((e: any) => {
        console.error('Failed to update rewards type', e);
      });
  };

  const addNew = () => {
    if (rows.length >= limit) return;
    const newRow: RowState = {
      id: 0,
      type: '',
      name: '',
      isNew: true,
      isEdited: false,
      draftType: '',
      draftName: ''
    };
    setRows([...rows, newRow]);
  };

  const onCancelNew = (index: number) => {
    const updated = [...rows];
    updated.splice(index, 1);
    setRows(updated);
    const updatedErrors = new Map(errors);
    updatedErrors.delete(index);
    setErrors(updatedErrors);
  };

  const onSaveNew = (index: number) => {
    const updated = [...rows];
    const row = updated[index];
    if (!validateOne(index, row.draftType, row.draftName)) return;

    const payload: RewardsTypeCreateRequest = {
      type: row.draftType.trim(),
      name: row.draftName.trim()
    } as RewardsTypeCreateRequest;

    createRewardsType(appID, payload)
      .then((resp: any) => {
        const data = resp?.data ?? resp;
        row.id = data.id;
        row.type = payload.type;
        row.name = payload.name;
        row.isNew = false;
        row.isEdited = false;
        updated[index] = { ...row };
        setRows(updated);
      })
      .catch((e: any) => {
        console.error('Failed to create rewards type', e);
      });
  };

  const onFieldChange = (index: number, field: 'type' | 'name', value: string) => {
    const updated = [...rows];
    const row = updated[index];
    if (field === 'type') row.draftType = value;
    if (field === 'name') row.draftName = value;
    if (row.isNew || row.isEdited) setRows(updated);
  };

  return (
    <>
      <FlexColumn gap={4}>
        <StyledCaption />
        <FlexRow justify={'flex-start'} align={'center'} gap={4}>
          <BodyText size={1}>Rewards types</BodyText>
          <StyledTooltip
            tooltipContent={
              <BodyText size={3} scale={'sm'}>
                Define simple <b>type</b> for rewards — these are used in the integration API and
                app code. Each type can also have an associated <b>name</b> for readability when
                configuring tasks or deals.
              </BodyText>
            }
          >
            <SvgIcon src={InfoIcon} color={'gray'} />
          </StyledTooltip>
        </FlexRow>
      </FlexColumn>

      <FlexColumn gap={8}>
        {rows.map((row, index) => (
          <React.Fragment key={row.id ?? `new-${index}`}>
            <FlexRow justify={'center'} align={'center'} gap={5}>
              <StyledInput
                label={index === 0 ? <BodyText size={1}>Type</BodyText> : ''}
                placeholder="type"
                disabled={!(row.isEdited || row.isNew)}
                height={'36'}
                value={row.draftType}
                onChange={(e) => onFieldChange(index, 'type', e.target.value)}
              />
              <StyledInput
                label={index === 0 ? <BodyText size={1}>Name</BodyText> : ''}
                placeholder="Name"
                disabled={!(row.isEdited || row.isNew)}
                height={'36'}
                value={row.draftName}
                onChange={(e) => onFieldChange(index, 'name', e.target.value)}
              />

              {!row.isNew && !row.isEdited && (
                <Button
                  color={'utility'}
                  style={{ marginTop: index != 0 ? '0' : '40px' }}
                  height={'36'}
                  width={'100'}
                  onClick={() => onEdit(index)}
                >
                  <StyledActionIcon icon="ri:pencil-fill" />
                </Button>
              )}

              {row.isEdited && (
                <FlexRow gap={5} style={{ marginTop: index != 0 ? '0' : '40px' }}>
                  <Button
                    color={'utility'}
                    height={'36'}
                    width={'100'}
                    onClick={() => onCancelEdit(index)}
                  >
                    Cancel
                  </Button>
                  <Button
                    color={'primaryBlue'}
                    height={'36'}
                    width={'100'}
                    onClick={() => onSaveEdit(index)}
                  >
                    Save
                  </Button>
                </FlexRow>
              )}

              {row.isNew && (
                <FlexRow gap={5} style={{ marginTop: index != 0 ? '0' : '40px' }}>
                  <Button
                    color={'utility'}
                    height={'36'}
                    width={'100'}
                    onClick={() => onCancelNew(index)}
                  >
                    Cancel
                  </Button>
                  <Button
                    color={'primaryBlue'}
                    height={'36'}
                    width={'100'}
                    onClick={() => onSaveNew(index)}
                  >
                    Save
                  </Button>
                </FlexRow>
              )}
            </FlexRow>

            {errors.has(index) && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors.get(index)}</ErrorText>
              </ErrorTextContainer>
            )}
          </React.Fragment>
        ))}

        <StyledAddElement
          size={3}
          scale={'md'}
          variation={rows.length >= limit ? 'gray' : 'blue'}
          isPointer={rows.length < limit}
        >
          <div onClick={addNew}> + Add Reward Type</div>
        </StyledAddElement>
      </FlexColumn>
    </>
  );
};

export default RewardsTypesControl;
